import { useMutation } from "@tanstack/react-query";

import { queryClient } from "@api/queryClient";
import { QUERY_KEYS } from "@api/queryKeys";
import { DRAFT_STATUS_CODE } from "@constants/draftStatusCode.constants";
import { getPressIdFromURL } from "@utils/getStateFromURL";
import { toastFail } from "@utils/toast";

import { dealsOverlappedUpdateApis } from "./dealsOverlappedUpdate.service";
import { PutArticleInfoRequest } from "./dealsOverlappedUpdate.type";

const getOverlappedData = (articleRejectReason: string) => ({
  publishable: false,
  articleRejectReason,
  draftArticleStatus: `${DRAFT_STATUS_CODE.unsuitable}`,
});

export const useDealsOverlappedUpdateMutation = (
  draftArticleId = Number(getPressIdFromURL()),
) => {
  const mutate = useMutation({
    mutationKey: [QUERY_KEYS.dealsOverlappedUpdate, getPressIdFromURL()],
    mutationFn: async ({
      targetId,
      similarId,
      isMcEditor,
      articleRejectReason,
      currentSimilarCategoryType,
      currentTargetCategoryType,
    }: {
      targetId: number;
      similarId: number;
      isMcEditor: boolean;
      articleRejectReason: string;
      currentSimilarCategoryType: "PRESS" | "FILING";
      currentTargetCategoryType: "PRESS" | "FILING";
    }) => {
      const overlappedData = getOverlappedData(articleRejectReason);
      const isDifferentCategoryType =
        currentSimilarCategoryType !== currentTargetCategoryType;
      if (isDifferentCategoryType) {
        const pressId =
          currentTargetCategoryType === "PRESS" ? targetId : similarId;
        const filingId =
          currentTargetCategoryType === "FILING" ? targetId : similarId;

        // target 과 similar 데이터가 다른경우
        // filing이 draftArticleId로 설정
        // press가 duplArticleId로 설정

        const defaultPressStatus = {
          ...overlappedData,
          draftArticleId: filingId,
          duplArticleId: pressId,
        };

        const mcEditorStatus = {
          ...overlappedData,
          draftArticleId: filingId,
          duplArticleId: pressId,
          type: "MC",
          isCheckStartDate: "true",
        } as PutArticleInfoRequest;

        const payload = isMcEditor ? defaultPressStatus : mcEditorStatus;

        const response = await dealsOverlappedUpdateApis.put(payload);
        return response;
      }

      const defaultPressStatus = {
        ...overlappedData,
        draftArticleId: targetId,
        duplArticleId: similarId,
      };

      const mcEditorStatus = {
        ...overlappedData,
        draftArticleId: targetId,
        duplArticleId: similarId,
        type: "MC",
        isCheckStartDate: "true",
      } as PutArticleInfoRequest;

      const payload = isMcEditor ? defaultPressStatus : mcEditorStatus;

      const response = await dealsOverlappedUpdateApis.put(payload);
      return response;
    },
    onSuccess: async (response, request) => {
      const {
        targetId,
        similarId,
        currentSimilarCategoryType,
        currentTargetCategoryType,
      } = request;
      if (response.code !== 200) {
        toastFail(response.message);
        throw new Error(response.message);
      }
      setTimeout(() => {
        const isDifferentCategoryType =
          currentSimilarCategoryType !== currentTargetCategoryType;
        if (isDifferentCategoryType) {
          const filingId =
            currentTargetCategoryType === "FILING" ? targetId : similarId;

          // target 과 similar 데이터가 다른경우
          // filing이 draftArticleId로 설정

          queryClient.refetchQueries({
            queryKey: [QUERY_KEYS.articleInfo, Number(filingId)],
          });
        }
        if (!isDifferentCategoryType) {
          queryClient.refetchQueries({
            queryKey: [QUERY_KEYS.articleInfo, Number(draftArticleId)],
          });
        }
      }, 100);
    },
  });
  return mutate;
};
